import { useEffect, useState } from "react";
import ApiLoader from "../../ui/ApiLoader";
import {
  fetchReports,
  fetchReportById,
  downloadReport,
} from "../../../services/axios/AuthApis/DashboardApis";
import { Eye, PenBoxIcon, Download } from "lucide-react";
import { toast } from "../../../utils/notify";
import { DataTable } from "../../common/DataTable";
// ================= Reports =================
const Reports = ({ activeTab }) => {
  const [reportsData, setReportsData] = useState([]);

  const [reportsLoading, setReportsLoading] = useState(false);

  const [selectedReport, setSelectedReport] = useState(null);

  const [showReportModal, setShowReportModal] = useState(false);

  const loadReports = async () => {
    try {
      setReportsLoading(true);

      const response = await fetchReports();

      console.log("Reports API:", response);

      setReportsData(response || []);
    } catch (err) {
      console.log(err);

      toast.error("Failed to load reports");
    } finally {
      setReportsLoading(false);
    }
  };

  const handleViewReport = async (id) => {
    try {
      const response = await fetchReportById(id);

      console.log("Report Details:", response);

      setSelectedReport(response);

      setShowReportModal(true);
    } catch (err) {
      console.log(err);

      toast.error("Unable to load report details");
    }
  };
  useEffect(() => {
    if (activeTab === "reports") {
      loadReports();
    }
  }, [activeTab]);

  const reportColumns = [
    {
      accessorKey: "report_name",
      header: "Report Name",
      meta: { filter: { variant: "text", label: "Report Name", placeholder: "Search report" } },
    },
    { accessorKey: "category", header: "Category" },
    { accessorKey: "generated_date", header: "Generated Date" },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const status = getValue();
        return (
          <span
            className={`px-3 py-1 rounded-full text-sm ${
              status === "completed"
                ? "bg-green-100 text-green-700"
                : status === "running"
                  ? "bg-yellow-100 text-yellow-700"
                  : "bg-red-100 text-red-700"
            }`}
          >
            {status}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Action",
      enableSorting: false,
      cell: ({ row }) => (
        <button onClick={() => handleViewReport(row.original.id)} className="p-2 border rounded hover:bg-gray-100">
          <Eye size={18} />
        </button>
      ),
    },
  ];

  

 if (reportsLoading) {
   return (
     <ApiLoader
       variant="spinner"
       label="Loading reports..."
       className="py-20"
     />
   );
 }
  return (
    activeTab === "reports" && (
      <>
        <h2 className="text-2xl font-bold mb-6">Reports</h2>
        {/* ================= Reports Summary Cards ================= */}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow p-5">
            <p className="text-gray-500">Total Reports</p>

            <h2 className="text-3xl font-bold mt-2 text-blue-600">
              {reportsData.length}
            </h2>
          </div>

          <div className="bg-white rounded-xl shadow p-5">
            <p className="text-gray-500">Completed</p>

            <h2 className="text-3xl font-bold mt-2 text-green-600">
              {reportsData.filter((item) => item.status === "completed").length}
            </h2>
          </div>

          <div className="bg-white rounded-xl shadow p-5">
            <p className="text-gray-500">Running</p>

            <h2 className="text-3xl font-bold mt-2 text-yellow-600">
              {reportsData.filter((item) => item.status === "running").length}
            </h2>
          </div>

          <div className="bg-white rounded-xl shadow p-5">
            <p className="text-gray-500">Failed</p>

            <h2 className="text-3xl font-bold mt-2 text-red-600">
              {reportsData.filter((item) => item.status === "failed").length}
            </h2>
          </div>
        </div>

        {/* ================= Report History Table ================= */}

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex justify-between items-center mb-5">
            <h2 className="text-xl font-bold">Report History</h2>

            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg">
              + Generate Report
            </button>
          </div>

          <DataTable
            columns={reportColumns}
            data={reportsData}
            getRowId={(item) => String(item.id)}
            loading={reportsLoading}
            emptyMessage="No Reports Found"
            enableColumnVisibility={false}
            enableColumnResizing
            enableColumnFilters
            maxBodyHeight="65vh"
          />
        </div>
        {/* ================= Report Details Modal ================= */}

        {showReportModal && selectedReport && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-[600px] p-6">
              <div className="flex justify-between items-center mb-5">
                <h2 className="text-xl font-bold">Report Details</h2>

                <button
                  onClick={() => {
                    setShowReportModal(false);

                    setSelectedReport(null);
                  }}
                  className="text-xl"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                {/* Description */}
                <div>
                  <h3 className="text-sm font-bold uppercase text-black mb-2">
                    Report Description
                  </h3>

                  <div className="border rounded-xl p-4 text-white bg-blue-600">
                    {selectedReport.description || "-"}
                  </div>
                </div>

                {/* Details */}
                <div className="grid grid-cols-2 gap-x-10 gap-y-5">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">
                      Report Name
                    </p>
                    <p className="font-medium">
                      {selectedReport.report_name}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 uppercase">
                      Category
                    </p>
                    <p className="font-medium">{selectedReport.category}</p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 uppercase">Status</p>

                    <span
                      className={`px-3 py-1 rounded-full ${
                        selectedReport.status === "completed"
                          ? "bg-green-100 text-green-700"
                          : selectedReport.status === "running"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-red-100 text-red-700"
                      }`}
                    >
                      {selectedReport.status}
                    </span>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 uppercase">
                      Generated Date
                    </p>
                    <p>{selectedReport.generated_date}</p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 uppercase">
                      Generated By
                    </p>
                    <p>{selectedReport.generated_by}</p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 uppercase">
                      Total Records
                    </p>
                    <p>{selectedReport.total_records}</p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 uppercase">
                      File Type
                    </p>
                    <p>{selectedReport.file_type}</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                {selectedReport.download_url && (
                  <a
                    href={selectedReport.download_url}
                    target="_blank"
                    rel="noreferrer"
                    className="bg-green-600 text-white px-5 py-2 rounded-lg"
                  >
                    <Download size={18} />
                  </a>
                )}

                <button
                  onClick={() => {
                    setShowReportModal(false);

                    setSelectedReport(null);
                  }}
                  className="bg-blue-600 text-white px-5 py-2 rounded-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    )
  );
};
export default Reports;
